import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Users,
  Clock,
  Settings,
  BarChart3,
  Calendar,
  Zap,
  CheckSquare,
  FileText,
  FileCheck,
  Layers,
  Activity,
  Search,
  MessageSquare,
} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import Footer from "../components/Footer";

const CASE_SUMMARY = [
  {
    icon: Building2,
    title: "Industry",
    desc: "Manufacturing (Operations)",
  },
  {
    icon: Users,
    title: "Key Users",
    desc: "Operators, Supervisors, Production Managers, Engineers",
  },
  {
    icon: Clock,
    title: "Business Need",
    desc: "Faster troubleshooting and better real-time decisions on the floor",
  },
  {
    icon: Settings,
    title: "Solution",
    desc: "Simpel Production Copilot",
  },
  {
    icon: BarChart3,
    title: "Impact",
    desc: "50-70% faster access to procedures",
  },
  {
    icon: Calendar,
    title: "Timeframe",
    desc: "9-15 months payback",
  },
];


const WHO_BENEFITS_DATA = [
  {
    role: "Plant Head / Operations Head",
    improves:
      "More stable output and fewer prolonged stoppages. Issues are resolved faster with standard guidance; production plans hold more reliably on the pilot area.",
  },
  {
    role: "Production Manager / Shift Lead",
    improves:
      "Faster troubleshooting and clearer decisions on shift. Teams find procedures and past learnings without delay; escalation is reserved for true exceptions.",
  },
  {
    role: "Operator / Technician",
    improves:
      "Immediate access to the right steps and information. Confidence on the job rises, dependence on a few experienced colleagues for routine answers falls.",
  },
  {
    role: "Engineering / Continuous Improvement",
    improves:
      "Standards and best practice used more widely. Knowledge captured in SOPs and logs is applied in real time, not only in training rooms.",
  },
];

const RESULTS_DATA = [
  {
    icon: Clock,
    subtitle: "Time to find the right procedure or troubleshooting step",
    title: "40% to 60% reduction",
  },
  {
    icon: Activity,
    subtitle: "Mean time to resolve routine operational issues (pilot area)",
    title: "25% to 40% shorter",
  },
  {
    icon: FileCheck,
    subtitle: "Escalations for information that already exists in SOPs / logs",
    title: "Material reduction",
  },
  {
    icon: BarChart3,
    subtitle: "Operator confidence /adherence to standard steps (pilot feedback)",
    title: "Clear improvement",
  },
  {
    icon: Calendar,
    subtitle: "Typical payback window",
    title: "9 - 15 months",
  },
  {
    icon: Layers,
    subtitle: "First-year value (faster recovery + decision quality)",
    title: "$150k - $400k (scale-dependent)",
  },
];

const PROCESS_STEPS = [
  {
    id: "ask",
    icon: MessageSquare,
    bgGradient: "bg-gradient-to-br from-[#93C5FD] to-[#3B82F6]",
    glow: "shadow-[0_4px_18px_rgba(59,130,246,0.35)]",
    title: "Ask",
    desc: "Enter a question in plain language",
  },
  {
    id: "find",
    icon: Search,
    bgGradient: "bg-gradient-to-br from-[#E9D5FF] to-[#A855F7]",
    glow: "shadow-[0_4px_18px_rgba(168,85,247,0.35)]",
    title: "Find",
    desc: "AI searches SOPs, guides and past logs.",
  },
  {
    id: "get-steps",
    icon: FileText,
    bgGradient: "bg-gradient-to-br from-[#FEF08A] to-[#EAB308]",
    glow: "shadow-[0_4px_18px_rgba(234,179,8,0.35)]",
    title: "Get Steps",
    desc: "Receive clear, cited guidance.",
  },
  {
    id: "resolve",
    icon: Settings,
    bgGradient: "bg-gradient-to-br from-[#BBF7D0] to-[#22C55E]",
    glow: "shadow-[0_4px_18px_rgba(34,197,94,0.35)]",
    title: "Resolve",
    desc: "Apply the steps on the floor.",
  },
  {
    id: "keep-running",
    icon: BarChart3,
    bgGradient: "bg-gradient-to-br from-[#FBCFE8] to-[#EC4899]",
    glow: "shadow-[0_4px_18px_rgba(236,72,153,0.35)]",
    title: "Keep running",
    desc: "The line stays on plan with confidence.",
  },
];

const ProductionCopilot = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/ai-opportunities?tab=Industries");
    }
  };

  // Scroll tracking across sticky hero track
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const overlayY = useTransform(
    scrollYProgress,
    [0, 0.22, 0.75, 0.98],
    ["100%", "0%", "0%", "-100%"]
  );

  const overlayOpacity = useTransform(
    scrollYProgress,
    [0, 0.06, 0.22, 0.75, 0.95, 1],
    [0, 0.6, 1, 1, 0.7, 0]
  );

  const handleScrollDown = () => {
    const el = document.getElementById("case-at-a-glance");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else if (containerRef.current) {
      const top = containerRef.current.offsetTop;
      const height = containerRef.current.offsetHeight;
      window.scrollTo({
        top: top + height,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#000000] flex flex-col text-white">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 w-full bg-[#000000] border-b border-white/[0.06] shadow-sm">
        <div className="mx-auto flex h-[76px] sm:h-[82px] max-w-[1440px] items-center justify-between px-6 lg:px-[70px]">
          {/* Simpel Brand Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 sm:gap-3.5 group select-none shrink-0"
          >
            <img
              src="/images/simpel-icon-mark.png"
              alt="Simpel"
              className="h-[34px] sm:h-[38px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col justify-center">
              <span className="text-[22px] sm:text-[25px] font-bold leading-none tracking-tight text-white">
                Simpel
              </span>
              <span className="mt-[3px] text-[7.5px] sm:text-[8px] font-bold uppercase tracking-[2.2px] sm:tracking-[2.5px] text-white/70">
                AUTOMATION SIMPLIFIED
              </span>
            </div>
          </Link>

          {/* Right Breadcrumb & Back Button */}
          <div className="flex items-center gap-5 sm:gap-8">
            <div className="hidden sm:block border-b-2 border-[#2F6DF6] pb-0.5">
              <span className="text-[14px] sm:text-[15px] font-medium text-[#2F6DF6] tracking-wide select-none">
                Industries / Manufacturing
              </span>
            </div>

            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full bg-[#00D2EE] px-5 py-2 sm:px-6 sm:py-2.5 text-[13px] sm:text-[14px] font-bold text-black transition-all duration-200 hover:brightness-105 hover:shadow-[0_4px_16px_rgba(0,210,238,0.35)] active:scale-95 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 stroke-[2.5]" />
              <span>Back to Cognitive AI</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full bg-[#000000]">
        {/* =========================================================================
            Hero Section with Scroll Pinning Animation (matching Screenshot 1)
            Background: /images/insights/production-copilot-hero.jpg
           ========================================================================= */}
        <section ref={containerRef} className="relative w-full h-[270vh]">
          {/* Sticky Viewport Stage */}
          <div className="sticky top-[76px] sm:top-[82px] h-[calc(100vh-76px)] sm:h-[calc(100vh-82px)] w-full overflow-hidden bg-[#000000]">
            {/* Underlying Hero Section Photo - 100% crystal clear at scroll 0 with NO tint */}
            <div className="absolute inset-0 w-full h-full">
              <img
                src="/images/insights/production-copilot-hero.jpg"
                alt="Production Copilot - Factory Floor Operations and Smart Terminal"
                className="w-full h-full object-cover object-center block select-none"
              />
            </div>

            {/* Frosted Blur Overlay Section with Pinning Animation */}
            <motion.div
              style={{
                y: overlayY,
                opacity: overlayOpacity,
              }}
              className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-20 text-center"
            >
              {/* Frosted Glass Background Tint matching Predictive Maintenance */}
              <div
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{
                  backgroundColor: "rgba(15, 27, 41, 0.94)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                }}
              />

              {/* Foreground Content */}
              <div className="relative z-10 max-w-[1080px] flex flex-col items-center pt-4 sm:pt-0">
                {/* Category Tag matching Screenshot 1 */}
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[3px] text-white/70 mb-5 sm:mb-6">
                  MANUFACTURING - OPERATIONS
                </p>

                {/* Main Heading with 2 Lines matching Screenshot 1 */}
                <h1 className="text-[32px] sm:text-[44px] md:text-[50px] lg:text-[54px] font-bold leading-[1.2] sm:leading-[1.16] text-white tracking-tight">
                  Production Copilot: Faster answers on{" "}
                  <span className="text-[#2B7FFF]">the</span>
                  <span className="block mt-1 sm:mt-1.5 bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent">
                    floor, better decisions in the moment
                  </span>
                </h1>

                {/* Subtitle Paragraph (3 Lines matching Screenshot 1) */}
                <div className="mt-5 sm:mt-6 max-w-[840px] text-[13.5px] sm:text-[15px] md:text-[15.5px] leading-[24px] sm:leading-[26px] text-white/80 text-center">
                  <p className="whitespace-normal sm:whitespace-nowrap">
                    How Simpel Production Copilot gives production teams instant access to information,
                  </p>
                  <p className="whitespace-normal sm:whitespace-nowrap mt-0.5">
                    helps them troubleshoot issues and supports faster operational decisions — so the line
                  </p>
                  <p className="whitespace-normal sm:whitespace-nowrap mt-0.5">
                    keeps moving with confidence.
                  </p>
                </div>

                {/* 4 Feature Pills in a horizontal row matching Screenshot 1 */}
                <div className="mt-8 sm:mt-10 flex flex-wrap items-center justify-center gap-6 sm:gap-10 max-w-[980px]">
                  {/* Pill 1 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <Zap className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Early fault<br className="hidden sm:inline" /> identification
                    </span>
                  </div>

                  {/* Pill 2 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <Calendar className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Planned maintenance<br className="hidden sm:inline" /> schedules
                    </span>
                  </div>

                  {/* Pill 3 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <Clock className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Higher equipment<br className="hidden sm:inline" /> uptime
                    </span>
                  </div>

                  {/* Pill 4 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <CheckSquare className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Data-driven maintenance<br className="hidden sm:inline" /> decisions
                    </span>
                  </div>
                </div>

                {/* Vertical Dashed Line with Downward Arrow */}
                <div
                  onClick={handleScrollDown}
                  className="mt-10 sm:mt-12 flex flex-col items-center cursor-pointer group"
                  title="Scroll to explore"
                >
                  <svg
                    width="10"
                    height="120"
                    viewBox="0 0 10 120"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="opacity-75 transition-opacity group-hover:opacity-100"
                  >
                    <line
                      x1="5"
                      y1="0"
                      x2="5"
                      y2="110"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                    />
                    <path
                      d="M5 110L10 102M5 110L0 102"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* =========================================================================
            1. Case at a glance Section (matching Screenshot 3)
            Background: rgba(245, 250, 254, 1)
            6 Overview Cards (2 rows x 3 cols)
           ========================================================================= */}
        <section
          id="case-at-a-glance"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-8 sm:mb-10">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Case at a glance
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* 6 Cards Grid (3 cols x 2 rows) matching Screenshot 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {CASE_SUMMARY.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-4 sm:gap-5 rounded-[14px] sm:rounded-[16px] bg-white border border-[#EAF2FA] p-6 sm:p-7 shadow-[0_2px_12px_rgba(15,23,42,0.02)] transition-all duration-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)] hover:-translate-y-0.5"
                  >
                    {/* Circular Blue Icon Badge */}
                    <div className="flex h-13 w-13 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                      <Icon className="h-6 w-6 stroke-[2]" />
                    </div>

                    {/* Text Details */}
                    <div className="flex flex-col gap-1.5 pt-0.5">
                      <span className="text-[12px] sm:text-[13px] font-bold uppercase tracking-wider text-[#0F172A]">
                        {card.title}
                      </span>
                      <p className="text-[13.5px] sm:text-[14px] leading-[21px] text-[#64748B]">
                        {card.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            2. The Context Section (matching Screenshot 4)
            Background: /images/insights/production-context-bg.jpg
            Left: Deep dark gradient overlay with heading & 2 paragraphs
           ========================================================================= */}
        <section
          id="the-context"
          className="relative w-full overflow-hidden bg-[#020B18] py-16 sm:py-20 lg:py-24 text-white"
        >
          {/* Base Background Image */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/production-context-bg.jpg"
              alt="Automated production bottling line context"
              className="h-full w-full object-cover object-center"
            />
          </div>

          {/* Left Solid Dark Base & Progressive Gradient Blur Overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, rgba(2,11,24,0.96) 0%, rgba(2,11,24,0.94) 40%, rgba(2,11,24,0.72) 60%, rgba(2,11,24,0.15) 85%, rgba(2,11,24,0) 100%)",
            }}
          />

          <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="max-w-[720px] lg:max-w-[760px]">
              {/* Header row with horizontal divider line matching Screenshot 4 */}
              <div className="flex items-center gap-6 mb-6 sm:mb-7">
                <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  The Context
                </h2>
                <div className="flex-1 h-[1px] bg-[#CEDDF1]/30" />
              </div>

              {/* Paragraph 1 matching Screenshot 4 */}
              <p className="text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[24px] sm:leading-[26px] text-white/85">
                Production teams run the plant in real time. When a parameter drifts, a machine alarms or a procedure is unclear, the speed of the right answer decides whether the line stays on plan.
              </p>

              {/* Paragraph 2 matching Screenshot 4 */}
              <p className="mt-5 sm:mt-6 text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[24px] sm:leading-[26px] text-white/85">
                SOPs, shift notes, equipment manuals and past incident learnings often exist — but finding them under pressure takes time. The outcome plants aim for is simple: less time searching, faster resolution of issues, and operational decisions grounded in the plant&apos;s own procedures and experience.
              </p>
            </div>
          </div>
        </section>

        
        {/* =========================================================================
            3. What the solution does Section (matching Screenshot 1)
            Background: rgba(245, 250, 254, 1)
            Left: Operator terminal card
            Right: What the solution does heading + 3 narrative paragraphs
           ========================================================================= */}
        <section
          id="what-the-solution-does"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* Left Column: Solution Operator Terminal Display Image */}
              <div className="lg:col-span-6 w-full flex justify-center">
                <div className="w-full overflow-hidden rounded-[14px] sm:rounded-[18px] shadow-[0_12px_40px_rgba(15,23,42,0.08)] bg-white border border-[#E2EDF8]">
                  <img
                    src="/images/insights/production-solution-operator.jpg"
                    alt="Production Copilot touchscreen terminal and operator"
                    className="w-full h-auto object-cover block select-none"
                  />
                </div>
              </div>

              {/* Right Column: Narrative Content */}
              <div className="lg:col-span-6 flex flex-col justify-center">
                {/* Heading row with horizontal divider line */}
                <div className="flex items-center gap-6 mb-5 sm:mb-6">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    What the solution does
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                {/* Paragraph 1 */}
                <p className="text-[13.5px] sm:text-[14px] leading-[23px] sm:leading-[25px] text-[#64748B]">
                  Operators, supervisors and engineers ask questions in plain language. The Copilot draws on approved SOPs, work instructions, equipment guides, shift logs and operational history to surface the most useful answers and steps.
                </p>

                {/* Paragraph 2 */}
                <p className="mt-4 sm:mt-5 text-[13.5px] sm:text-[14px] leading-[23px] sm:leading-[25px] text-[#64748B]">
                  Troubleshooting guidance is available without waiting for the one person who &ldquo;knows that machine.&rdquo; Decisions on the floor stay aligned with plant standards.
                </p>

                {/* Paragraph 3 */}
                <p className="mt-4 sm:mt-5 text-[13.5px] sm:text-[14px] leading-[23px] sm:leading-[25px] text-[#64748B]">
                  Integration respects the plant&apos;s systems and security rules so the insight sits in the same operational flow as production and CMMS work orders.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            4. The challenge Section (matching Screenshot 2)
            Background: #FFFFFF (White)
            Left: The challenge heading (gradient) + divider line + 3 lines intro copy
            Right: Two numbered challenge cards (01 Information under pressure, 02 Impact on production)
           ========================================================================= */}
        <section
          id="the-challenge"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* Left Column: Heading and intro copy */}
              <div className="lg:col-span-6 flex flex-col justify-center">
                <div className="flex items-center gap-6 mb-5 sm:mb-6">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    The challenge
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                <div className="text-[13.5px] sm:text-[14px] leading-[23px] sm:leading-[25px] text-[#64748B] max-w-[560px]">
                  <p className="whitespace-normal sm:whitespace-nowrap">
                    Every minute on the floor needs a clear next step. The challenge is to
                  </p>
                  <p className="whitespace-normal sm:whitespace-nowrap mt-0.5">
                    give production teams instant, trusted access to the right information so
                  </p>
                  <p className="whitespace-normal sm:whitespace-nowrap mt-0.5">
                    they can troubleshoot faster and keep the line moving.
                  </p>
                </div>
              </div>

              {/* Right Column: 2 Numbered Problem Cards */}
              <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                {/* Card 01 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#00D2EE] tracking-tight leading-none">
                      01
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Information under pressure
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      Finding the right SOP, guide or past incident takes time during a live issue.
                    </p>
                  </div>
                </div>

                {/* Card 02 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#00D2EE] tracking-tight leading-none">
                      02
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Impact on production
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      Delays in troubleshooting can lead to prolonged stoppages and lost output.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            5. Who benefits Section (matching Screenshot 3)
            Background: /images/insights/production-copilot-hero.jpg
            Left: Who benefits heading (gradient) + horizontal line (#CEDDF1) + subtitle
            Table: 4 Roles with glassmorphic styling (rgba(11,22,44,0.5), rgba(30,41,59,1), blur 30px)
           ========================================================================= */}
        <section
          id="who-benefits"
          className="relative w-full overflow-hidden bg-[#020B18] py-16 sm:py-20 lg:py-24 text-white"
        >
          {/* Base Background Image */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/production-copilot-hero.jpg"
              alt="Who Benefits Background"
              className="h-full w-full object-cover object-center"
            />
          </div>

          {/* Dark Glass Overlay */}
          <div className="absolute inset-0 bg-[#020B18]/85 backdrop-blur-[2px] pointer-events-none" />

          <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with gradient text and horizontal divider matching Screenshot (#CEDDF1) */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Who benefits
              </h2>
              <div className="flex-1 h-[1px] bg-[#CEDDF1]" />
            </div>

            {/* Subtitle matching Screenshot 3 */}
            <p className="text-[13.5px] sm:text-[14.5px] leading-[22px] text-white/80 mb-8 sm:mb-10 max-w-[840px]">
              Four roles feel the difference when support is instant and trusted.
            </p>

            {/* Roles Table Container matching Figma specs */}
            <div
              style={{
                backgroundColor: "rgba(9, 21, 40, 0.28)",
                borderColor: "rgba(30, 41, 59, 1)",
                borderWidth: "1px",
                borderStyle: "solid",
                boxShadow:
                  "0 8px 10px -6px rgba(0, 0, 0, 0.1), 0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
              }}
              className="w-full rounded-[14px] overflow-hidden"
            >
              {/* Table Header */}
              <div
                style={{
                  backgroundColor: "rgba(14, 29, 53, 0.65)",
                  borderBottomColor: "rgba(30, 41, 59, 1)",
                }}
                className="grid grid-cols-12 px-6 py-4 border-b text-[11px] sm:text-[12px] font-bold tracking-[1.5px] uppercase text-white/90"
              >
                <div className="col-span-12 sm:col-span-4 lg:col-span-4">Role</div>
                <div className="hidden sm:block sm:col-span-8 lg:col-span-8">What improves for them</div>
              </div>

              {/* Table Rows */}
              <div className="divide-y" style={{ borderColor: "rgba(30, 41, 59, 1)" }}>
                {WHO_BENEFITS_DATA.map((item) => (
                  <div
                    key={item.role}
                    style={{ borderColor: "rgba(30, 41, 59, 1)" }}
                    className="grid grid-cols-12 px-6 py-5 sm:py-6 items-start gap-2 sm:gap-6 hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="col-span-12 sm:col-span-4 lg:col-span-4 text-[14.5px] sm:text-[15.5px] font-bold text-white leading-snug">
                      {item.role}
                    </div>
                    <div className="col-span-12 sm:col-span-8 lg:col-span-8 text-[13px] sm:text-[13.5px] leading-[22px] text-white/80">
                      <span className="sm:hidden text-[11px] uppercase tracking-wider text-slate-400 font-semibold block mb-1">
                        What improves:
                      </span>
                      {item.improves}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            6. Results plants typically see Section (matching Screenshot 4)
            Background: rgba(245, 250, 254, 1)
            6 Metric Cards (3 cols x 2 rows)
           ========================================================================= */}
        <section
          id="results-plants-typically-see"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-8 sm:mb-10">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Results plants typically see
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* 6 Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {RESULTS_DATA.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-4 sm:gap-5 rounded-[14px] sm:rounded-[16px] bg-white border border-[#EAF2FA] p-6 sm:p-7 shadow-[0_2px_12px_rgba(15,23,42,0.02)] transition-all duration-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)] hover:-translate-y-0.5"
                  >
                    {/* Circular Blue Icon Badge */}
                    <div className="flex h-13 w-13 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                      <Icon className="h-6 w-6 stroke-[2]" />
                    </div>

                    {/* Text Details */}
                    <div className="flex flex-col gap-1.5 pt-0.5">
                      <span className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {card.subtitle}
                      </span>
                      <h3 className="text-[15.5px] sm:text-[16.5px] font-bold text-[#0F172A] leading-snug">
                        {card.title}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            7. How it works in practice Section (matching Screenshot 5)
            Background: #FFFFFF (White)
            5-Step Process Flow: Ask -> Find -> Get Steps -> Resolve -> Keep running
            Bottom: Production Manager Quote Box
           ========================================================================= */}
        <section
          id="how-it-works-in-practice"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                How it works in practice
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* Subtitle matching Screenshot 5 */}
            <p className="text-[13.5px] sm:text-[14px] lg:text-[14.5px] leading-[23px] text-[#64748B] mb-12 sm:mb-16 max-w-[1040px]">
              A parameter drifts or an alarm appears. The operator or supervisor asks the Production Copilot. Relevant steps from SOPs, equipment guidance and past operational notes appear with sources. The team acts within standard practice. The issue is resolved sooner; the line returns to plan.
            </p>

            {/* 5-Step Process Flow with Connectors */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-2">
              {PROCESS_STEPS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={step.id}>
                    {/* Step Item */}
                    <div className="flex flex-col items-center text-center max-w-[210px] w-full group">
                      {/* Colored Circle Badge */}
                      <div
                        className={`flex h-16 w-16 sm:h-18 sm:w-18 items-center justify-center rounded-full ${step.bgGradient} ${step.glow} text-white transition-transform duration-300 group-hover:scale-110 mb-4 sm:mb-5`}
                      >
                        <Icon className="h-7 w-7 sm:h-8 sm:w-8 stroke-[2.2]" />
                      </div>

                      {/* Step Title */}
                      <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] mb-2">
                        {step.title}
                      </h3>

                      {/* Step Description */}
                      <p className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {step.desc}
                      </p>
                    </div>

                    {/* Connecting Arrow between steps (hidden on last step) */}
                    {idx < PROCESS_STEPS.length - 1 && (
                      <div className="hidden lg:flex items-center justify-center shrink-0 px-2 text-[#00D2EE]">
                        <ArrowRight className="h-6 w-6 stroke-[2.5]" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Quote Callout Box matching Screenshot 5 */}
            <div className="mt-12 sm:mt-16 w-full rounded-[14px] sm:rounded-[18px] bg-[#F5FAFE] border border-[#E2EDF8] py-6 sm:py-7 px-6 sm:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 shadow-[0_2px_12px_rgba(15,23,42,0.02)]">
              <div className="flex items-start gap-3.5">
                <span className="text-[#007AFF] text-[32px] sm:text-[40px] font-serif leading-none select-none -mt-1">
                  &ldquo;
                </span>
                <p className="text-[14.5px] sm:text-[15.5px] font-medium text-[#1E293B] leading-[25px] sm:leading-[27px]">
                  &ldquo;When something went off plan, we used to wait for the right person or dig through folders. Now the team gets the standard steps in seconds. We still decide — we just decide with the plant&apos;s knowledge in front of us.&rdquo;
                </p>
              </div>

              <div className="shrink-0 lg:border-l-2 lg:border-[#007AFF] lg:pl-6 pt-4 lg:pt-0 border-t lg:border-t-0 border-[#E2EDF8]">
                <span className="block text-[14px] sm:text-[14.5px] font-bold text-[#0F172A] leading-tight">
                  Production Manager
                </span>
                <span className="block text-[12px] sm:text-[12.5px] text-[#64748B] mt-1">
                  Manufacturing Plant
                </span>
              </div>
            </div>
          </div>
        </section>

        
        {/* =========================================================================
            8. CTA Banner Section (matching Screenshot)
            Background: /images/insights/production-context-bg.jpg
            Heading: "Turn operation knowledge into higher uptime." in 1 single line without clipping
           ========================================================================= */}
        <section
          id="cta-banner"
          className="relative w-full overflow-hidden bg-[#050B14] flex items-center min-h-[340px] lg:h-[340px] text-white"
        >
          {/* Base Background Image: Pinned to the right side so conveyor line and tower are clear and never overlap text */}
          <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[54%] lg:w-[48%] xl:w-[46%] h-full pointer-events-none select-none overflow-hidden">
            <img
              src="/images/insights/production-context-bg.jpg"
              alt="Turn operation knowledge into higher uptime"
              className="h-full w-full object-cover object-right"
            />
          </div>

          {/* Linear Gradient Overlay: seamless dark fade to prevent collision */}
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #050B14 0%, #050B14 42%, rgba(5, 11, 20, 0.92) 54%, rgba(5, 11, 20, 0) 72%)",
            }}
          />

          {/* Figma Progressive Blur Layer */}
          <div
            className="absolute inset-y-0 left-0 w-[60%] pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, rgba(5, 11, 20, 0.8) 0%, rgba(5, 11, 20, 0.6) 45%, rgba(5, 11, 20, 0) 65%)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              maskImage: "linear-gradient(to right, black 45%, transparent 65%)",
              WebkitMaskImage: "linear-gradient(to right, black 45%, transparent 65%)",
            }}
          />

          {/* Content Container (Max width 1440px matching layout) */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-10 sm:py-12 lg:py-0 flex flex-col justify-center">
            <div className="max-w-[800px] xl:max-w-[880px] w-full">
              {/* Heading: exact 1 line on desktop with full width */}
              <h2 className="text-[24px] sm:text-[27px] md:text-[30px] lg:text-[32px] xl:text-[34px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent leading-[1.25] whitespace-normal sm:whitespace-nowrap inline-block">
                Turn operation knowledge into higher uptime.
              </h2>

              {/* Subtitle */}
              <p className="mt-3.5 sm:mt-4 text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[22px] sm:leading-[24px] text-white/85 max-w-[620px]">
                See how Simpel Production Copilot can help your plant troubleshoot faster and keep production on plan.
              </p>

              {/* Consultation Button */}
              <div className="mt-6 sm:mt-7">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 sm:px-7 sm:py-3 text-[13.5px] sm:text-[14px] font-bold text-[#0F172A] transition-all duration-200 hover:bg-slate-100 hover:shadow-[0_8px_24px_rgba(255,255,255,0.25)] hover:scale-105 active:scale-95"
                >
                  <span>Request a Consultation</span>
                  <ArrowRight className="h-4 w-4 stroke-[2.5]" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Anchor for future sections */}
        <div id="content-start" />
      </main>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default ProductionCopilot;

